/**
 * VectorSpace.js — the in-app, full-page 3-D semantic vector-space explorer.
 *
 * Hosts the framework-agnostic three.js scene (see ./vectorSpaceScene.js), fed by
 * `GET /api/analysis/vector-space` (which reads the app's pgvector embeddings).
 * A project selector scopes the map to one project's requirements at a time, so
 * each project's semantic space is shown on its own rather than all mixed together.
 * Reachable from the main side nav ("Vector Space").
 *
 * #261/#262 (F3): there used to be an "All projects" option that auto-loaded on
 * mount with no project selected — analysing the WHOLE tenant's embeddings in one
 * kmeans/MDS pass, which doesn't make product sense (a cross-project semantic map
 * mixes unrelated requirement sets) and was the likely cause of a reported server
 * crash (unbounded compute on a memory-constrained dyno). The backend now 400s a
 * request with no project scope at all, so this view REQUIRES an explicit project
 * pick before ever calling the API — no "All projects" option, no auto-fetch.
 */
import * as React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import { API_BASE_URL } from '../services/api';
import { getActiveOrg, getActiveProject } from '../services/authToken';
import iamApi from '../services/iamApi';
import { mountVectorSpace } from './vectorSpaceScene';
import './vectorSpace.css';

export default function VectorSpace() {
  const rootRef = React.useRef(null);
  const [state, setState] = React.useState('picking'); // 'picking' | 'loading' | 'ready' | 'error'
  const [projects, setProjects] = React.useState([]);
  // null = no project chosen yet (nothing is fetched while this is null).
  const [selected, setSelected] = React.useState(null);

  // Load the org's real (tenancy) projects — NOT root items — so only actual
  // projects are listed. Default to the active project if set; if there's
  // exactly one project, that's the only real choice, so pick it automatically
  // (matches the same "single project = no ambiguity" convention used elsewhere,
  // e.g. the tree's add-dialog project resolution). Otherwise leave unselected —
  // the picker is shown and nothing loads until the user chooses.
  React.useEffect(() => {
    let cancelled = false;
    const org = getActiveOrg();
    const fetchProjects = org ? iamApi.myProjects(org) : Promise.resolve([]);
    fetchProjects
      .then((list) => {
        if (cancelled) return;
        const projs = (list || []).map((p) => ({ id: p.id, name: p.name || p.title || p.id }));
        setProjects(projs);
        const active = getActiveProject();
        if (projs.some((p) => p.id === active)) setSelected(active);
        else if (projs.length === 1) setSelected(projs[0].id);
      })
      .catch(() => { if (!cancelled) setProjects([]); });
    return () => { cancelled = true; };
  }, []);

  // (Re)mount the scene whenever a real project is selected. Never fires with
  // no selection — 'picking' just sits and shows the prompt below.
  React.useEffect(() => {
    if (!selected) return undefined;
    let handle;
    let cancelled = false;
    const el = rootRef.current;
    if (!el) return undefined;
    setState('loading');
    mountVectorSpace(el, {
      apiBase: API_BASE_URL,
      projectId: selected,
      onStatus: (s) => !cancelled && setState(s),
    })
      .then((h) => { if (cancelled) { h.dispose(); } else { handle = h; } })
      .catch((e) => { if (!cancelled) setState('error'); console.error('VectorSpace mount failed', e); });
    return () => { cancelled = true; if (handle) handle.dispose(); };
  }, [selected]);

  return (
    <Box sx={{ position: 'relative', flexGrow: 1, height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {/* Project selector — shown whenever there's a real choice to make (or to
          confirm) — no "All projects" option (#261/#262). */}
      {projects.length > 0 && (
        <FormControl
          size="small"
          sx={{
            position: 'absolute', top: 12, right: 12, zIndex: 12, minWidth: 200,
            bgcolor: 'background.paper', borderRadius: 1, boxShadow: 2,
          }}
        >
          <InputLabel id="vs-project-label">Project</InputLabel>
          <Select
            labelId="vs-project-label"
            label="Project"
            displayEmpty
            value={selected ?? ''}
            onChange={(e) => setSelected(e.target.value)}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!selected && (
        <Box
          data-testid="vector-space-pick-prompt"
          sx={{
            position: 'absolute', inset: 0, zIndex: 10, display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 1, color: 'text.secondary', textAlign: 'center', px: 3,
          }}
        >
          <Typography variant="body1">
            {projects.length === 0 ? 'No projects to show yet.' : 'Select a project to view its vector space.'}
          </Typography>
        </Box>
      )}

      {state === 'loading' && (
        <Box
          sx={{
            position: 'absolute', inset: 0, zIndex: 10, display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 2, color: 'text.secondary', pointerEvents: 'none',
          }}
        >
          <CircularProgress size={28} />
          <Typography variant="body2">Embedding &amp; laying out the requirement space…</Typography>
        </Box>
      )}
      <div ref={rootRef} style={{ width: '100%', height: '100%' }} />
    </Box>
  );
}
